import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, GraduationCap, Calendar, Users, MapPin, Sparkles, X } from "lucide-react";
import { useReference } from "@/lib/reference";

const STATUS_COLOR = {
  Scheduled: "bg-[#5D3A1F]/10 text-[#5D3A1F]",
  "In Progress": "bg-[#D4A547]/15 text-[#A6741F]",
  Completed: "bg-[#6B8E5A]/15 text-[#6B8E5A]",
  Cancelled: "bg-[#A23E3E]/15 text-[#A23E3E]",
};

const DEFAULT_REFLECTION_QUESTIONS = [
  "What is the most important thing I learned today?",
  "How will I apply this in my role this week?",
  "One thing I will do differently starting now",
  "Any questions or thoughts I still have",
];

const emptyWS = {
  name: "", client_name: "", client_id: "", service_type: "Customer Service Excellence",
  date: new Date().toISOString().slice(0, 10), location: "", participants_count: 0,
  facilitator_notes: "", materials: "", rating: null, status: "Scheduled", follow_up_actions: "",
  contract_value: 0, invoice_amount: 0, amount_collected: 0,
  reflection_questions: [...DEFAULT_REFLECTION_QUESTIONS],
  session_questions: {}, // { "1": ["Q1","Q2","Q3","Q4"], ... } — blanks fall back to default
};

const WorkshopForm = ({ open, onOpenChange, initial, onSubmit, mode, clients }) => {
  const ref = useReference();
  const [form, setForm] = useState(() => {
    const base = initial || emptyWS;
    return {
      ...base,
      reflection_questions: (base.reflection_questions && base.reflection_questions.length === 4)
        ? base.reflection_questions
        : [...DEFAULT_REFLECTION_QUESTIONS],
    };
  });
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiContent, setAiContent] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const handleOpenChange = (v) => {
    if (v) {
      const base = initial || emptyWS;
      setForm({
        ...base,
        reflection_questions: (base.reflection_questions && base.reflection_questions.length === 4)
          ? base.reflection_questions
          : [...DEFAULT_REFLECTION_QUESTIONS],
        session_questions: base.session_questions || {},
      });
    }
    onOpenChange(v);
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQ = (idx, value) => setForm((f) => {
    const q = [...(f.reflection_questions || DEFAULT_REFLECTION_QUESTIONS)];
    q[idx] = value;
    return { ...f, reflection_questions: q };
  });
  const setSessionQ = (sessionNum, idx, value) => setForm((f) => {
    const map = { ...(f.session_questions || {}) };
    const arr = map[String(sessionNum)] ? [...map[String(sessionNum)]] : ["", "", "", ""];
    arr[idx] = value;
    // If every slot is blank, remove the override so it falls back to default
    if (arr.every((v) => !v.trim())) {
      delete map[String(sessionNum)];
    } else {
      map[String(sessionNum)] = arr;
    }
    return { ...f, session_questions: map };
  });
  const clearSession = (sessionNum) => setForm((f) => {
    const map = { ...(f.session_questions || {}) };
    delete map[String(sessionNum)];
    return { ...f, session_questions: map };
  });
  const resetQs = () => setF("reflection_questions", [...DEFAULT_REFLECTION_QUESTIONS]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.participants_count = Number(payload.participants_count || 0);
      payload.rating = payload.rating ? Number(payload.rating) : null;
      payload.contract_value = Number(payload.contract_value || 0);
      payload.invoice_amount = Number(payload.invoice_amount || 0);
      payload.amount_collected = Number(payload.amount_collected || 0);
      payload.reflection_questions = (payload.reflection_questions || []).map(
        (q, i) => (q && q.trim()) || DEFAULT_REFLECTION_QUESTIONS[i],
      );
      await onSubmit(payload);
      handleOpenChange(false);
    } finally { setSaving(false); }
  };

  const generateOutline = async () => {
    setAiLoading(true); setAiOpen(true); setAiContent("");
    try {
      const res = await api.post("/ai/workshop-outline", {
        workshop_type: form.service_type, audience: "Workshop participants",
        duration: "1 day", objectives: form.facilitator_notes,
      });
      setAiContent(res.data.content);
    } catch (e) {
      toast.error("AI generation failed");
      setAiOpen(false);
    } finally { setAiLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="workshop-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{mode === "create" ? "Schedule workshop" : "Edit workshop"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-2 md:col-span-2">
            <Label>Workshop Name *</Label>
            <Input data-testid="ws-name-input" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Customer Service Excellence Q1" />
          </div>
          <div className="space-y-2">
            <Label>Client</Label>
            <Select value={form.client_id || "none"} onValueChange={(v) => {
              if (v === "none") { setF("client_id", ""); setF("client_name", ""); return; }
              const c = clients.find((x) => x.id === v);
              setF("client_id", v); setF("client_name", c?.organization_name || "");
            }}>
              <SelectTrigger data-testid="ws-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Service Type</Label>
            <Select value={form.service_type} onValueChange={(v) => setF("service_type", v)}>
              <SelectTrigger data-testid="ws-service-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ref.services.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Date *</Label><Input data-testid="ws-date-input" type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} /></div>
          <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setF("location", e.target.value)} /></div>
          <div className="space-y-2"><Label># Participants</Label><Input type="number" min="0" value={form.participants_count} onChange={(e) => setF("participants_count", e.target.value)} /></div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setF("status", v)}>
              <SelectTrigger data-testid="ws-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ref.workshop_statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Rating (1-5)</Label><Input type="number" min="0" max="5" step="0.5" value={form.rating || ""} onChange={(e) => setF("rating", e.target.value)} /></div>

          <div className="md:col-span-2 pt-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold mb-1">Financials</div>
            <div className="text-xs text-[#6B5944]">Track the engagement&apos;s contract &amp; collection. Used by the Finance module.</div>
          </div>
          <div className="space-y-2"><Label>Contract Value (USD)</Label><Input type="number" min="0" step="0.01" value={form.contract_value} onChange={(e) => setF("contract_value", e.target.value)} data-testid="ws-contract-input" /></div>
          <div className="space-y-2"><Label>Invoice Amount (USD)</Label><Input type="number" min="0" step="0.01" value={form.invoice_amount} onChange={(e) => setF("invoice_amount", e.target.value)} data-testid="ws-invoice-input" /></div>
          <div className="space-y-2 md:col-span-2"><Label>Amount Collected (USD)</Label><Input type="number" min="0" step="0.01" value={form.amount_collected} onChange={(e) => setF("amount_collected", e.target.value)} data-testid="ws-collected-input" /></div>

          {/* Reflection Questions — used by per-session QR forms */}
          <div className="md:col-span-2 pt-3 border-t border-[#EBE3D2]">
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Reflection Questions</div>
                <div className="text-xs text-[#6B5944] mt-0.5">Used on QR Code 1 (per-session reflection forms). Customize for this engagement, or leave the defaults.</div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={resetQs} data-testid="ws-reset-questions">Reset to defaults</Button>
            </div>
          </div>
          {(form.reflection_questions || DEFAULT_REFLECTION_QUESTIONS).map((q, idx) => (
            <div key={idx} className="md:col-span-2 space-y-1.5">
              <Label className="text-xs flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#C28A2E] text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                Default Question {idx + 1}
              </Label>
              <Textarea
                data-testid={`ws-reflection-q${idx + 1}`}
                rows={2}
                value={q}
                onChange={(e) => setQ(idx, e.target.value)}
                placeholder={DEFAULT_REFLECTION_QUESTIONS[idx]}
              />
            </div>
          ))}

          {/* Per-session question overrides */}
          <div className="md:col-span-2 pt-4 mt-2 border-t border-[#EBE3D2]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Per-Session Overrides (optional)</div>
            <div className="text-xs text-[#6B5944] mt-0.5">
              Leave a session blank to use the defaults above. Override only the sessions where you want different questions.
            </div>
            <Tabs defaultValue="1" className="w-full mt-3">
              <TabsList className="bg-[#EBE3D2]/50 rounded-xl">
                {[1, 2, 3, 4].map((n) => {
                  const has = (form.session_questions?.[String(n)] || []).some((v) => (v || "").trim());
                  return (
                    <TabsTrigger key={n} value={String(n)} data-testid={`ws-session-tab-${n}`} className="rounded-lg">
                      Session {n}
                      {has && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-[#C28A2E]" />}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {[1, 2, 3, 4].map((n) => {
                const sq = form.session_questions?.[String(n)] || ["", "", "", ""];
                const hasOverride = sq.some((v) => (v || "").trim());
                return (
                  <TabsContent key={n} value={String(n)} className="mt-4 space-y-3">
                    {hasOverride && (
                      <div className="flex justify-between items-center text-xs text-[#6B5944]">
                        <span>Custom questions set for Session {n}.</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => clearSession(n)} data-testid={`ws-clear-session-${n}`}>
                          Clear (use defaults)
                        </Button>
                      </div>
                    )}
                    {[0, 1, 2, 3].map((idx) => (
                      <div key={idx} className="space-y-1.5">
                        <Label className="text-xs flex items-center gap-2 text-[#5D3A1F]">
                          <span className="w-5 h-5 rounded-full bg-[#5D3A1F] text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                          Session {n} · Question {idx + 1}
                        </Label>
                        <Textarea
                          data-testid={`ws-session-${n}-q${idx + 1}`}
                          rows={2}
                          value={sq[idx] || ""}
                          onChange={(e) => setSessionQ(n, idx, e.target.value)}
                          placeholder={`Leave blank to use default: "${(form.reflection_questions || DEFAULT_REFLECTION_QUESTIONS)[idx]}"`}
                        />
                      </div>
                    ))}
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>

          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Facilitator Notes</Label>
              <Button type="button" variant="outline" size="sm" onClick={generateOutline} data-testid="ws-ai-outline-btn"
                className="text-[#C28A2E] border-[#C28A2E]/30 hover:bg-[#C28A2E]/5">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI workshop outline
              </Button>
            </div>
            <Textarea rows={3} value={form.facilitator_notes} onChange={(e) => setF("facilitator_notes", e.target.value)} />
          </div>
          <div className="space-y-2 md:col-span-2"><Label>Training Materials</Label><Textarea rows={2} value={form.materials} onChange={(e) => setF("materials", e.target.value)} /></div>
          <div className="space-y-2 md:col-span-2"><Label>Follow-Up Actions</Label><Textarea rows={2} value={form.follow_up_actions} onChange={(e) => setF("follow_up_actions", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="ws-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="ws-form-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>

        {aiOpen && (
          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C28A2E]" /> AI Workshop Outline
              </DialogTitle></DialogHeader>
              {aiLoading ? <div className="py-10 text-center text-[#6B5944]">Drafting outline…</div> : (
                <div className="ccs-prose text-sm" data-testid="ai-outline-content">
                  <pre className="whitespace-pre-wrap font-sans text-sm">{aiContent}</pre>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setAiOpen(false)}>Close</Button>
                <Button onClick={() => { setF("facilitator_notes", aiContent); setAiOpen(false); }} className="bg-[#5D3A1F] hover:bg-[#4A2E18]">
                  Use as notes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

const WorkshopsPage = () => {
  const [workshops, setWorkshops] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([api.get("/workshops"), api.get("/clients")])
      .then(([w, c]) => { setWorkshops(w.data); setClients(c.data); })
      .finally(() => setLoading(false));
  };
  useEffect(fetchAll, []);

  const onCreate = async (d) => { try { await api.post("/workshops", d); toast.success("Workshop scheduled"); fetchAll(); } catch (e) { toast.error("Failed"); throw e; } };
  const onUpdate = async (d) => { try { await api.put(`/workshops/${editing.id}`, d); toast.success("Updated"); fetchAll(); } catch (e) { toast.error("Failed"); throw e; } };
  const onDelete = async (id) => { if (!window.confirm("Delete this workshop?")) return; await api.delete(`/workshops/${id}`); toast.success("Deleted"); fetchAll(); };

  return (
    <div className="space-y-6" data-testid="workshops-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Training</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Workshops & Engagements</h1>
          <p className="text-[#6B5944] mt-1.5">From scheduled to delivered — track every training engagement.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-workshop-btn"
          className="bg-[#C28A2E] hover:bg-[#A6741F] text-white rounded-xl h-11 px-5 hover:-translate-y-[1px] transition-all">
          <Plus className="w-4 h-4 mr-2" /> New workshop
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#6B5944]">Loading…</div>
      ) : workshops.length === 0 ? (
        <div className="ccs-card p-12 text-center">
          <GraduationCap className="w-10 h-10 text-[#C28A2E] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display text-xl font-medium text-[#2A1810]">No workshops yet</div>
          <p className="text-[#6B5944] text-sm mt-1">Schedule your first workshop to start tracking your training engagements.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workshops.map((w) => (
            <div key={w.id} className="ccs-card ccs-card-hover p-5" data-testid={`workshop-card-${w.id}`}>
              <div className="flex items-start justify-between">
                <Badge className={`${STATUS_COLOR[w.status]} rounded-full font-medium px-3`}>{w.status}</Badge>
                <div className="flex gap-1 -mr-2">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(w); setFormOpen(true); }} data-testid={`edit-workshop-${w.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(w.id)}><Trash2 className="w-4 h-4 text-[#A23E3E]" /></Button>
                </div>
              </div>
              <div className="font-display text-lg font-semibold mt-3 text-[#2A1810] leading-tight">{w.name}</div>
              <div className="text-sm text-[#6B5944] mt-1">{w.service_type}</div>
              <div className="space-y-1.5 mt-4 text-xs text-[#6B5944]">
                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {w.date}</div>
                {w.client_name && <div className="flex items-center gap-2 truncate">{w.client_name}</div>}
                {w.location && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {w.location}</div>}
                <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {w.participants_count} participants</div>
                {w.rating && <div className="text-[#D4A547] font-medium">★ {w.rating} / 5</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <WorkshopForm open={formOpen} onOpenChange={setFormOpen} initial={editing}
        mode={editing ? "edit" : "create"} onSubmit={editing ? onUpdate : onCreate} clients={clients} />
    </div>
  );
};

export default WorkshopsPage;
